import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  Chart,
  ChartDataset,
  ChartOptions,
  ChartType,
  LinearScale,
  LinearScaleOptions,
  Plugin,
} from 'chart.js';

import { BaseChartDirective } from 'ng2-charts';
import {
  firstValueFrom,
  forkJoin,
  of,
  timer,
  Subject,
  Subscription,
} from 'rxjs';
import { catchError, skip, take, takeUntil, tap } from 'rxjs/operators';
import {
  DataAvailableDateRange,
  DataService,
  ManagementService,
  SensorsSensor,
  SensorsSensorfield,
  SensorsService,
  TrigonosDatasource,
} from 'src/app/api/generated';
import 'chartjs-adapter-moment';

import { ModuleConfiguration } from 'src/app/app.module';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import { UserService } from 'src/app/user.service';
import { GenericCorrelationConfigComponent } from '../config/correlation-config/correlation-config.component';
import {
  GenericCorrelationConfig,
  DataPointGnss,
  getSelectableTimePeriods,
  getSelectableLowPassFilterPeriods,
  getMinMaxFromConfig,
  DoubleKeyMap,
  computeZeroFilteredDataset,
  ChartJSNoDataPlugin,
} from '../correlation.tools';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CsvExportService } from 'src/app/csv-export.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatTooltipValue } from 'src/app/tools/validators';
import { CorrelationLegendComponent } from '../correlation-legend/correlation-legend.component';
import {
  GlobalTimeSettingsService,
  GlobalDateRange,
  SmoothingValue,
  DateRangeKind,
} from 'src/app/services/global-time-settings/global-time-settings.service';

interface CorrelationChartPoint {
  x: string | number | Date;
  y: number;
}

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    textPlugin?: {
      text: string;
      position: { x: number; y: number };
      fontColor?: string;
      fontSize?: string;
      fontFamily?: string;
    };
  }
}

@Component({
    selector: 'app-correlation-generic',
    templateUrl: './correlation.component.html',
    styleUrls: ['./correlation.component.scss'],
    standalone: false
})
export class GenericCorrelationComponent implements OnInit, OnDestroy {
  @Input() config: GenericCorrelationConfig;
  @ViewChild(BaseChartDirective, { static: false })
  chart: BaseChartDirective<'line', CorrelationChartPoint[], unknown>;
  @ViewChild(CorrelationLegendComponent, { static: false })
  legendComponent: CorrelationLegendComponent;

  public static moduleDetails: ModuleConfiguration = {
    displayName: 'Zeit-Verformungsgraphik',
    previewImagePath: 'assets/correlation_preview.png',
    settingsComponent: GenericCorrelationConfigComponent,
  };

  public isLoading = true;
  public chartData: ChartDataset<'line', CorrelationChartPoint[]>[] = [];
  public chartPlugins: Plugin<'line'>[] = [ChartJSNoDataPlugin()];
  private loadedDatasets: Map<string, DataPointGnss[]> = new Map<
    string,
    DataPointGnss[]
  >();
  public chartWidth = '100%';

  public correlationForm: UntypedFormGroup;
  public selectedFieldControl = new UntypedFormControl();
  public lowPassDateRange = new UntypedFormControl();

  public selectableTimePeriods: any[];
  public selectableLowPassFilterPeriods: any[];
  public preDefinedDatePeriod = new UntypedFormControl();
  public preDefinedLowPassFilterPeriods = new UntypedFormControl();

  public datasetDetails: Map<string, TrigonosDatasource> = new Map<
    string,
    TrigonosDatasource
  >();

  public loadedData: DoubleKeyMap<string, string, []> = new DoubleKeyMap<
    string, // sensor
    string, // field
    [] // data
  >();

  // Dataset visibility tracking
  private datasetVisibilityMap = new Map<string, boolean>();
  public chartInstance: Chart<'line', CorrelationChartPoint[], unknown>;

  // Second y-axis data
  public secondYAxisData: any[] = [];

  public maxDate: Date; // = new Date(Date.now());
  public minDate: Date; //= new Date(0);
  public customDateRange = false;
  private lastCustomDateRange = Date.now();
  private lastAppliedDateStart: any = null;
  private lastAppliedDateEnd: any = null;
  private destroy$ = new Subject<void>();
  private secondYAxisLoadCancel$ = new Subject<void>();
  private mainAxisLoadCancel$ = new Subject<void>();
  public relativeZeroFiltering = false;

  public minMaxDates: Map<string, DataAvailableDateRange> = new Map<
    string,
    DataAvailableDateRange
  >();

  public chartOptions: ChartOptions<'line'> = {};
  onInitFinished: boolean = false;

  // Global time settings tracking - used to prevent initial data load from overwriting settings
  private globalSettingsApplied = false;
  private globalSettingsSubscriptions: Subscription[] = [];

  constructor(
    private readonly dataService: DataService,
    private datePipe: DatePipe,
    private formBuilder: UntypedFormBuilder,
    public readonly reportModuleService: ReportModulesService,
    private managementService: ManagementService,
    public userService: UserService,
    private csvExportService: CsvExportService,
    private sensorService: SensorsService,
    private spinner: NgxSpinnerService,
    private matSnackBar: MatSnackBar,
    private translateService: TranslateService,
    private globalTimeSettingsService: GlobalTimeSettingsService
  ) {
    this.selectableTimePeriods = getSelectableTimePeriods(
      this.translateService
    );
    this.preDefinedDatePeriod.setValue(this.selectableTimePeriods[5].value);
    this.selectableLowPassFilterPeriods = getSelectableLowPassFilterPeriods(
      this.translateService
    );
    this.preDefinedLowPassFilterPeriods.setValue(
      this.selectableLowPassFilterPeriods[0].value
    );
  }

  private beforeScaleFit(scale) {
    // https://stackoverflow.com/questions/37004611/how-to-update-chartjs2-option-scale-tick-max
    const { startDate, endDate } = this.getStartEndDate();
    if (this.customDateRange && startDate && endDate) {
      scale.options.suggestedMin = startDate;
      scale.options.suggestedMax = endDate;
    } else if (
      this.preDefinedDatePeriod.value !== -1 &&
      new Date(this.preDefinedDatePeriod.value).getTime() !==
        new Date(0).getTime()
    ) {
      scale.options.suggestedMin =
        this.preDefinedDatePeriod.value.toISOString();
      scale.options.suggestedMax = new Date(Date.now()).toISOString();
    } else {
      scale.options.suggestedMin = undefined;
      scale.options.suggestedMax = new Date(Date.now()).toISOString();
    }
  }

  private beforeYScaleFit(scale: LinearScale) {
    if (!this.correlationForm.get('selectedField').value?.identifier) {
      return;
    }
    const minMax = getMinMaxFromConfig(
      this.correlationForm.get('selectedField').value.identifier,
      this.config.min_max
    );

    if (minMax.min !== 0) {
      scale.options.min = minMax.min;
    } else {
      scale.options.min = undefined;
    }

    if (minMax.max !== 0) {
      scale.options.max = minMax.max;
    } else {
      scale.options.max = undefined;
    }
  }

  private beforeSecondYScaleFit(scale: LinearScale) {
    if (
      !this.config.secondYAxis?.enabled ||
      !this.config.secondYAxis?.min_max
    ) {
      return;
    }

    const minMax = this.config.secondYAxis.min_max;

    if (minMax.min !== 0) {
      scale.options.min = minMax.min;
    } else {
      scale.options.min = undefined;
    }

    if (minMax.max !== 0) {
      scale.options.max = minMax.max;
    } else {
      scale.options.max = undefined;
    }

    // Sync tick count with primary axis
    if (this.chart?.chart?.scales?.y) {
      const primaryScale = this.chart.chart.scales.y as LinearScale;
      if (primaryScale.ticks && primaryScale.ticks.length > 0) {
        // Set the tick count to match the primary axis
        if (scale.options.ticks) {
          scale.options.ticks.count = primaryScale.ticks.length;
        }
      }
    }
  }

  public availableFields: Map<string, SensorsSensorfield> = new Map<
    string,
    SensorsSensorfield
  >();

  public availableSensors: Map<string, SensorsSensor> = new Map<
    string,
    SensorsSensor
  >();

  onDataLoadedComplete = () => {
    // Handle field selection for both initial load and subsequent data reloads
    if (!this.onInitFinished) {
      // Initial load: set default values
      const fieldNames = this.config.sensorFields.map(
        (field) => field.fieldName
      );

      if (fieldNames.length > 0) {
        let defaultField = null;

        if (this.config.defaultSelectedField) {
          // First, try to find by Map key (field.fieldName)
          if (this.availableFields.has(this.config.defaultSelectedField)) {
            defaultField = this.availableFields.get(this.config.defaultSelectedField);
          } else {
            // Fallback: iterate through available fields to find match by identifier
            for (const field of this.availableFields.values()) {
              if (field.identifier === this.config.defaultSelectedField) {
                defaultField = field;
                break;
              }
            }
          }
        }

        // Set the default field if found, otherwise use first available
        if (defaultField) {
          this.selectedFieldControl.setValue(defaultField);
        } else if (this.availableFields.size > 0) {
          this.selectedFieldControl.setValue(
            this.availableFields.get(Array.from(this.availableFields.keys())[0])
          );
        }
      }

      // Mark initialization as finished after setting default field
      this.onInitFinished = true;
    } else {
      // Subsequent data reloads: preserve current selection or set to first available
      const currentValue = this.selectedFieldControl.value;

      // If no current value or current value is no longer available, set to first available
      if (!currentValue || !this.availableFields.has(currentValue.identifier)) {
        if (this.availableFields.size > 0) {
          this.selectedFieldControl.setValue(
            this.availableFields.get(Array.from(this.availableFields.keys())[0])
          );
        }
      }
    }

    this.loadDatasetAndVisualise();

    // Load second y-axis data after main axis data is processed
    // This ensures the second axis uses the same date range as the main axis
    this.loadSecondYAxisData();

    this.isLoading = false;
  };

  public async loadData(initialLoading: boolean) {
    const allRequests = [];

    // Always load Field Information and Sensor Information if not already loaded
    if (initialLoading || this.availableFields.size === 0) {
      // Load Field Information
      const sensorFieldInfoRequests = this.config.sensorFields.map((field) =>
        this.sensorService.sensorsFieldRead(field.fieldName).pipe(
          tap((result) => {
            if (this.fieldHasEnabledSensors(field.fieldName)) {
              this.availableFields.set(field.fieldName, result);
            }
          })
        )
      );

      // Load Information about the sensors
      const sensorInfoRequests = this.config.sensorFields
        .map((field) =>
          field.sensors
            .filter((sensor) => sensor.checked)
            .map((sensor) => {
              return this.sensorService
                .sensorsSensorRead(sensor.influx_name)
                .pipe(
                  tap((result) => {
                    if (sensor.checked) {
                      this.availableSensors.set(sensor.influx_name, result);
                    }
                  })
                );
            })
        )
        .flat();

      allRequests.push(...sensorFieldInfoRequests);
      allRequests.push(...sensorInfoRequests);
    }

    // Always load the data
    const { startDate, endDate } = this.getStartEndDate();
    const { hourStart, hourEnd } = this.getHourFilter();

    const dataRequests = this.config.sensorFields
      .map((field) =>
        field.sensors
          .filter((sensor) => sensor.checked)
          .map((sensor) => {
            return this.dataService
              .dataMeasurementsList(
                field.fieldName,
                sensor.influx_name,
                startDate,
                endDate,
                hourStart,
                hourEnd,
                this.correlationForm.get('aggregation').value,
                this.correlationForm.get('preDefinedLowPassFilter').value
              )
              .pipe(
                tap((result) => {
                  if (sensor.checked) {
                    this.loadedData.set(
                      field.fieldName,
                      sensor.influx_name,
                      result as any
                    );
                  }
                })
              );
          })
      )
      .flat();

    // Note: Second y-axis data is now loaded separately after main axis data is processed
    // This ensures the second axis uses the same date range as the main axis

    allRequests.push(...dataRequests);

    const allRequestsHandled = allRequests.map((req$) =>
      req$.pipe(
        catchError((err) => {
          // Probably fix this in the future
          return of(null);
        })
      )
    );

    const merged$ = forkJoin([...allRequestsHandled]);

    merged$
      .pipe(takeUntil(this.destroy$), takeUntil(this.mainAxisLoadCancel$))
      .subscribe({
        complete: () => this.onDataLoadedComplete(),
      });
  }

  async ngOnInit() {
    this.spinner.show();

    this.correlationForm = this.formBuilder.group({
      selectedField: this.selectedFieldControl,
      preDefinedDateField: this.preDefinedDatePeriod,
      preDefinedLowPassFilter: this.preDefinedLowPassFilterPeriods,
      aggregation: [true],
      filterDateStart: [
        new Date(new Date().setMonth(new Date().getMonth() - 1)),
      ],
      filterDateEnd: [new Date()],
    });

    // Smoothing: config default > global fallback > hardcoded '0'
    if (this.config?.defaultLowPassFilter) {
      this.preDefinedLowPassFilterPeriods.setValue(
        this.config.defaultLowPassFilter
      );
    } else {
      this.applyGlobalSmoothing(this.globalTimeSettingsService.getCurrentSmoothing());
    }

    // Initialize date range from config.defaultDateRange if present
    if (this.config?.defaultDateRange) {
      const ddr = this.config.defaultDateRange;
      if (ddr.kind === 'custom') {
        this.customDateRange = true;
        this.preDefinedDatePeriod.setValue(-1);
        const start = ddr.custom?.start
          ? new Date(ddr.custom.start)
          : new Date(0);
        const end = ddr.custom?.end ? new Date(ddr.custom.end) : new Date();
        this.correlationForm.controls['filterDateStart'].setValue(start);
        this.correlationForm.controls['filterDateEnd'].setValue(end);
      } else {
        this.customDateRange = false;
        // Use the exact option value reference from selectableTimePeriods so the mat-select preselects
        const kindToIndex = (k: string): number => {
          switch (k) {
            case '24h':
              return 0;
            case '7d':
              return 1;
            case '14d':
              return 2;
            case '1m':
              return 3;
            case '1y':
              return 4;
            case 'zero':
            default:
              return 5; // Von Nullmessung
          }
        };
        const idx = kindToIndex(ddr.kind);
        if (
          Array.isArray(this.selectableTimePeriods) &&
          this.selectableTimePeriods[idx]
        ) {
          this.preDefinedDatePeriod.setValue(
            this.selectableTimePeriods[idx].value
          );
        }
      }
    } else {
      // No config default - apply global settings as fallback
      this.applyGlobalDateRange(this.globalTimeSettingsService.getCurrentDateRange());
    }
    this.globalSettingsApplied = true;

    // Subscribe to global time settings changes (skip initial value since we already applied it)
    // "Last one wins" - global changes always apply, overriding any local changes
    this.globalSettingsSubscriptions.push(
      this.globalTimeSettingsService.dateRange$.pipe(skip(1)).subscribe((dateRange) => {
        this.applyGlobalDateRange(dateRange);
        if (this.onInitFinished) {
          this.reloadGraph(true);
        }
      })
    );

    this.globalSettingsSubscriptions.push(
      this.globalTimeSettingsService.smoothing$.pipe(skip(1)).subscribe((smoothing) => {
        this.applyGlobalSmoothing(smoothing);
        if (this.onInitFinished) {
          this.reloadGraph(true);
        }
      })
    );

    await this.loadData(true);

    this.beforeScaleFit = this.beforeScaleFit.bind(this);
    this.beforeYScaleFit = this.beforeYScaleFit.bind(this);
    this.beforeSecondYScaleFit = this.beforeSecondYScaleFit.bind(this);

    this.chartOptions = {
      locale: 'de-AT',
      // devicePixelRatio: 10,
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      // stacked: false,
      elements: {
        point: {
          // borderWidth: 0,
          // pointStyle: 'crossRot',
          // pointStyle: ''
        },
      },
      plugins: {
        textPlugin: {
          text: this.translateService.instant('app.noDataInTimePeriod'),
          fontColor: 'rgba(0,0,0,0.4)',
        },
        legend: {
          display: false, // Hide default legend since we're using custom one
        },
        tooltip: {
          enabled: true,
          position: 'nearest',
          intersect: true,
          callbacks: {
            title: (object) => {
              const data = object[0];
              return this.datePipe.transform(
                data.dataset.data[data.dataIndex]['x'],
                'short',
                undefined,
                'de-AT'
              );
            },
            label: (data: any) => {
              let label = data.dataset.label || '';
              if (label) {
                label += ': ';
              }
              label += formatTooltipValue(
                data.dataset.data[data.dataIndex]['y'] as number
              );

              // Add unit information based on which axis the dataset belongs to
              if (data.dataset.yAxisID === 'y1') {
                // Second y-axis dataset
                if (this.config.secondYAxis?.fieldName) {
                  const secondYAxisField = this.availableFields.get(
                    this.config.secondYAxis.fieldName
                  );
                  if (secondYAxisField?.field_unit) {
                    label += ` ${secondYAxisField.field_unit}`;
                  }
                }
              } else {
                // Primary y-axis dataset
                const suffix =
                  this.selectedFieldControl.value?.field_unit || '';
                if (suffix) {
                  label += ` ${suffix}`;
                }
              }

              return label;
            },
          },
        },
      },

      animation: {
        duration: 0,
      },
      hover: {},
      responsive: true,
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          beforeFit: this.beforeYScaleFit,
          title: {
            display: true,
            text: this.config.y_label,
          },
          ticks: {
            callback: (value: string | number, index, values) => {
              const numValue = parseFloat(value as string);
              const formattedValue = formatTooltipValue(numValue, 3);

              const suffix = this.selectedFieldControl.value?.field_unit || '';
              return `${formattedValue} ${suffix}`;
            },
            includeBounds: true,
          },
        },
        y1: {
          type: 'linear',
          display: this.config.secondYAxis?.enabled || false,
          position: 'right',
          beforeFit: this.beforeSecondYScaleFit,
          grid: {
            drawOnChartArea: false, // only want the grid lines for one axis to show up
          },
          title: {
            display: this.config.secondYAxis?.enabled || false,
            text: this.config.secondYAxis?.label || '',
          },
          ticks: {
            callback: (value: string | number, index, values) => {
              const numValue = parseFloat(value as string);
              const formattedValue = formatTooltipValue(numValue, 3);

              // Get the field unit for the second y-axis
              let suffix = '';
              if (
                this.config.secondYAxis?.enabled &&
                this.config.secondYAxis?.fieldName
              ) {
                const secondYAxisField = this.availableFields.get(
                  this.config.secondYAxis.fieldName
                );
                suffix = secondYAxisField?.field_unit || '';
              }
              return `${formattedValue} ${suffix}`;
            },
            includeBounds: true,
          },
        },
        x: {
          beforeFit: this.beforeScaleFit,
          type: 'time',
          bounds: 'ticks',
          ticks: {
            source: 'auto',
            includeBounds: true,
          },
          time: {
            displayFormats: {
              hour: 'DD.MM.YYYY - HH:mm',
              day: 'DD.MM.YYYY',
              week: 'DD.MM.YYYY',
            },
          },
          title: {
            display: true,
            text: this.config.x_label,
          },
        },
      },
    };

    // Note: onInitFinished is now set in onDataLoadedComplete() after initial field selection

    // Ensure chart instance is available for legend component
    setTimeout(() => {
      this.onChartReady();
      this.updateLegend();
    }, 100);
  }

  private getHourFilter(): { hourStart: number; hourEnd: number } {
    if (this.config.filter.enabled) {
      return {
        hourStart: this.config.filter.hourStart,
        hourEnd: this.config.filter.hourEnd,
      };
    }
    return { hourStart: 0, hourEnd: 23 };
  }
  private getStartEndDate(): { startDate: string; endDate: string } {
    let startDate: string;
    let endDate: string;
    if (this.customDateRange || this.preDefinedDatePeriod.value === -1) {
      let filterDateStartValue =
        this.correlationForm.controls['filterDateStart'].value;
      let filterDateEndValue =
        this.correlationForm.controls['filterDateEnd'].value;

      // Convert the date object to a moment object
      filterDateStartValue = moment(filterDateStartValue);
      filterDateEndValue = moment(filterDateEndValue);

      startDate = filterDateStartValue
        ? filterDateStartValue.startOf('day').utcOffset(0, true).toISOString()
        : new Date(0).toISOString();
      endDate = filterDateEndValue
        ? filterDateEndValue.startOf('day').utcOffset(0, true).toISOString()
        : new Date(0).toISOString();
    } else {
      startDate = this.preDefinedDatePeriod.value.toISOString();
      endDate = new Date(Date.now()).toISOString();
    }
    return { startDate, endDate };
  }

  /**
   * Extract the actual date range from loaded main axis data
   * This ensures the second axis uses the same date range as the main axis
   */
  private getMainAxisDateRange(): {
    startDate: string;
    endDate: string;
  } | null {
    if (!this.selectedFieldControl.value) {
      return null;
    }

    const selectedField = this.selectedFieldControl.value as SensorsSensorfield;
    const fieldData = this.loadedData.map.get(selectedField.identifier);

    if (!fieldData || fieldData.size === 0) {
      return null;
    }

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    // Iterate through all sensors for the selected field
    for (const [sensorName, dataArray] of fieldData) {
      if (dataArray && dataArray.length > 0) {
        for (const dataPoint of dataArray as any[]) {
          const dataDate = new Date(dataPoint._time);

          if (minDate === null || dataDate < minDate) {
            minDate = dataDate;
          }
          if (maxDate === null || dataDate > maxDate) {
            maxDate = dataDate;
          }
        }
      }
    }

    if (minDate && maxDate) {
      return {
        startDate: minDate.toISOString(),
        endDate: maxDate.toISOString(),
      };
    }

    return null;
  }

  /**
   * Load second y-axis data using the date range from main axis data
   * This ensures the second axis is constrained to the same date range as the main axis
   */
  private loadSecondYAxisData(): void {
    if (
      !this.config.secondYAxis?.enabled ||
      !this.config.secondYAxis?.sensor ||
      !this.config.secondYAxis?.fieldName
    ) {
      return;
    }

    // Get the actual date range from main axis data
    const mainAxisDateRange = this.getMainAxisDateRange();
    if (!mainAxisDateRange) {
      console.warn(
        'Could not determine main axis date range for second y-axis'
      );
      return;
    }

    const { hourStart, hourEnd } = this.getHourFilter();

    // Cancel any in-flight second-y-axis request from a prior reload so its
    // response can't land after chartData has been cleared and duplicate a series.
    this.secondYAxisLoadCancel$.next();

    // Load second y-axis data with the same date range as main axis
    this.dataService
      .dataMeasurementsList(
        this.config.secondYAxis.fieldName,
        this.config.secondYAxis.sensor.influx_name,
        mainAxisDateRange.startDate,
        mainAxisDateRange.endDate,
        hourStart,
        hourEnd,
        true, // Always use aggregation for second y-axis
        '0' // No smoothing filter for second y-axis
      )
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.secondYAxisLoadCancel$),
        tap((result) => {
          this.secondYAxisData = result as any;
          // Update the chart with the new second y-axis data
          this.updateSecondYAxisGraph();
          this.updateChartOptionsForSecondYAxis();
          this.chart.chart.update();
          this.updateLegend();
        }),
        catchError((err) => {
          console.error('Error loading second y-axis data:', err);
          return of(null);
        })
      )
      .subscribe();
  }

  private loadDatasetAndVisualise() {
    // Currently selected field
    const selectedField = this.selectedFieldControl.value as SensorsSensorfield;

    const minMaxDate = this.minMaxDates.get(selectedField.identifier);

    // Only set date range values on initial load when no global settings have been applied
    if (minMaxDate && !this.onInitFinished) { // && !this.globalSettingsApplied) {
      if (!this.customDateRange) {
        this.correlationForm.controls['filterDateStart'].setValue(
          minMaxDate.start_date
        );
        this.correlationForm.controls['filterDateEnd'].setValue(
          minMaxDate.end_date
        );
      }
    }

    for (const key of this.loadedData.map.get(selectedField.identifier).keys()) {
      this.updateGraph(selectedField.identifier, key);
    }

    // Add second y-axis dataset if enabled
    if (this.config.secondYAxis?.enabled && this.secondYAxisData.length > 0) {
      this.updateSecondYAxisGraph();
    }

    // Update chart options for second y-axis
    this.updateChartOptionsForSecondYAxis();

    this.chart.chart.update();

    // Restore dataset visibility state after chart update
    this.restoreDatasetVisibility();

    // Update the chart instance reference for the legend component
    this.updateLegendComponent();

    // Force update the legend component
    this.updateLegend();
  }

  public updateGraph(field: string, sensor: string) {
    let dataset = this.loadedData.get(field, sensor) as any;

    if (!dataset) {
      return;
    }

    if (this.relativeZeroFiltering) {
      dataset = computeZeroFilteredDataset(dataset, field);
    }

    const datapoints: CorrelationChartPoint[] = dataset.map((datapoint) => ({
      x: datapoint._time,
      y: datapoint[this.selectedFieldControl.value.identifier],
    }));

    const datapointColor = this.availableSensors.get(sensor).color;

    const data: ChartDataset<'line', CorrelationChartPoint[]> = {
      data: datapoints,
      label: `${this.availableSensors.get(sensor).display_name}`,
      fill: false,
      tension: 0,
      pointBorderWidth: 0,
      pointBorderColor: 'rgba(0, 0, 0, 0)',
      pointRadius: 1.5,
      borderWidth: 1.5,
      pointHoverRadius: 3,
      pointBackgroundColor: datapointColor,
      backgroundColor: datapointColor,
      borderColor: datapointColor,
      yAxisID: 'y', // Explicitly assign to primary y-axis
    };

    this.chartData.push(data);

    if (this.chart) {
      // this.chart.updateColors();
      this.chart.chart.update();

      // Force update the legend
      this.updateLegend();
    }
  }

  public updateSecondYAxisGraph() {
    if (!this.config.secondYAxis?.enabled || !this.secondYAxisData.length) {
      return;
    }

    // Second y-axis data is always raw, no relative zero filtering
    const dataset = this.secondYAxisData;

    const datapoints: CorrelationChartPoint[] = dataset.map((datapoint) => ({
      x: datapoint._time,
      y: datapoint[this.config.secondYAxis.fieldName],
    }));

    // Use a different color for the second y-axis (you can customize this)
    const secondYAxisColor = '#87CEEB'; // Light blue color for second y-axis

    const data: ChartDataset<'line', CorrelationChartPoint[]> = {
      data: datapoints,
      label: `${this.config.secondYAxis.sensor.display_name} (${this.config.secondYAxis.label})`,
      fill: false,
      tension: 0,
      pointBorderWidth: 0,
      pointBorderColor: 'rgba(0, 0, 0, 0)',
      pointRadius: 1.5,
      borderWidth: 1.5,
      pointHoverRadius: 3,
      pointBackgroundColor: secondYAxisColor,
      backgroundColor: secondYAxisColor,
      borderColor: secondYAxisColor,
      yAxisID: 'y1', // This assigns the dataset to the second y-axis
    };

    this.chartData.push(data);

    if (this.chart) {
      this.chart.chart.update();

      // Force update the legend
      this.updateLegend();
    }
  }

  private updateChartOptionsForSecondYAxis() {
    if (this.chart && this.chart.chart) {
      const chart = this.chart.chart;

      // Update y1 axis display
      if (chart.scales.y1) {
        const y1Options = chart.scales.y1.options as LinearScaleOptions;
        y1Options.display = this.config.secondYAxis?.enabled || false;
        if (y1Options.title) {
          y1Options.title.display =
            this.config.secondYAxis?.enabled || false;
          y1Options.title.text = this.config.secondYAxis?.label || '';
        }
      }

      chart.update();
    }
  }

  public selectionChanged(event) {
    this.reloadGraph(true);
  }

  public selectionParameterChanged(event) {
    // Parameter changes should be immediate without data reloading
    this.reloadGraph(false);
  }

  public relativeZeroChanged(event: MatSlideToggleChange) {
    this.relativeZeroFiltering = event.checked;
    this.reloadGraph();
  }

  private loadDateRange(): Promise<void> {
    // Load date ranges
    const dateRangeRequests = this.config.sensorFields
      .map((field) =>
        field.sensors
          .filter((sensor) => sensor.checked)
          .map((sensor) => {
            return this.dataService
              .dataDaterangeInfluxList(sensor.influx_name, field.fieldName)
              .pipe(
                tap((result) => {
                  result = {
                    start_date: new Date(result.start_date),
                    end_date: new Date(result.end_date),
                  } as any;

                  if (!this.minMaxDates.has(field.fieldName)) {
                    this.minMaxDates.set(field.fieldName, result);
                  } else {
                    const currentMinMax = this.minMaxDates.get(field.fieldName);
                    if (result.end_date > currentMinMax.end_date) {
                      currentMinMax.end_date = result.end_date;
                    }
                    if (result.start_date < currentMinMax.start_date) {
                      currentMinMax.start_date = result.start_date;
                    }
                  }
                })
              );
          })
      )
      .flat();

    const dateRangeRequestsHandled = dateRangeRequests.map((req$) =>
      req$.pipe(
        catchError((err) => {
          // Probably fix this in the future
          return of(null);
        })
      )
    );

    const merged$ = forkJoin([...dateRangeRequestsHandled]);

    return new Promise<void>((resolve) => {
      merged$.subscribe({
        complete: () => {
          // Date range metadata loaded - this populates minMaxDates
          // The graph will be reloaded by dateRangeSelectionChanged() after this completes
          this.customDateRange = true;
          // Don't set isLoading = false here - let reloadGraph() handle the loading state
          resolve();
        },
      });
    });
  }

  private applySelectedFieldAvailableDateRange(): void {
    const selectedField = this.selectedFieldControl.value as SensorsSensorfield;
    const availableDateRange = selectedField
      ? this.minMaxDates.get(selectedField.identifier)
      : undefined;

    if (!availableDateRange) {
      return;
    }

    const startDate = new Date(availableDateRange.start_date);
    const endDate = new Date(availableDateRange.end_date);

    this.minDate = startDate;
    this.maxDate = endDate;
    this.correlationForm.controls['filterDateStart'].setValue(startDate);
    this.correlationForm.controls['filterDateEnd'].setValue(endDate);
  }

  public async dateRangeSelectionChanged(event) {
    if (event.value === -1) {
      // Start loading screen
      this.customDateRange = true;
      this.isLoading = true;
      await this.loadDateRange();
      this.applySelectedFieldAvailableDateRange();
      this.reloadGraph(true);
    } else {
      this.customDateRange = false;
      this.reloadGraph(true);
    }
  }

  public customDateFilterChanged(event) {
    this.lastCustomDateRange = Date.now();

    setTimeout(() => {
      const filterDateStartValue = this.correlationForm.controls['filterDateStart'].value;
      const filterDateEndValue = this.correlationForm.controls['filterDateEnd'].value;

      if (!filterDateStartValue || !filterDateEndValue) {
        console.warn('Custom date filter changed but both dates are not set yet. Start:', filterDateStartValue, 'End:', filterDateEndValue);
        return;
      }

      const startKey = moment.isMoment(filterDateStartValue) ? filterDateStartValue.valueOf() : +new Date(filterDateStartValue);
      const endKey = moment.isMoment(filterDateEndValue) ? filterDateEndValue.valueOf() : +new Date(filterDateEndValue);

      if (this.lastAppliedDateStart === startKey && this.lastAppliedDateEnd === endKey) {
        return;
      }
      this.lastAppliedDateStart = startKey;
      this.lastAppliedDateEnd = endKey;

      this.reloadGraph(true);
    }, 150);
  }

  private reloadGraph(dateChange: boolean = false) {
    this.isLoading = true;
    this.spinner.show();

    // Cancel any main-axis load still in flight from a prior reloadGraph.
    // Without this, two Apply-driven reloads can both resolve and push their
    // series into chartData, duplicating every parameter.
    this.mainAxisLoadCancel$.next();

    // Preserve current dataset visibility state before clearing
    this.preserveDatasetVisibility();

    this.emptyArray(this.chartData);
    this.emptyArray(this.secondYAxisData);

    // Fetch all the data again with the new date range
    if (dateChange) {
      this.isLoading = true;
      this.loadedData.clear();
      // Don't clear availableFields and availableSensors - they don't change during data reloads
      // this.availableFields.clear();
      // this.availableSensors.clear();

      this.loadData(true);
    } else {
      // For non-date changes (like relative zero toggle), we need to reload data
      // to ensure second y-axis data is preserved
      this.loadData(false);
    }

    this.loadedDatasets.clear();
  }

  private emptyArray(array: any[]) {
    while (array.length) {
      array.pop();
    }
  }

  // Set white background color so the download looks nice
  // https://stackoverflow.com/a/50126796/
  private fillCanvasBackgroundWithColor(canvas, color) {
    const context = canvas.getContext('2d');
    context.save();
    context.globalCompositeOperation = 'destination-over';
    context.fillStyle = color;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.restore();
  }

  public async exportCurrentView(event) {
    this.chart.chart.options.devicePixelRatio = 2;
    this.chartWidth = '1920px';
    let countdown = 0;
    while (countdown < 2) {
      await firstValueFrom(timer(100).pipe(take(1)));
      countdown++;
    }
    this.fillCanvasBackgroundWithColor(this.chart.chart.canvas, '#FFFFFF');

    const a = document.createElement('a');
    a.href = this.chart.chart.toBase64Image('image/png', 1);
    const date = new Date();
    a.download = `${this.reportModuleService.currentReport.name}_${date
      .toLocaleDateString('de-AT')
      .replace(/\./g, '_')}.png`;

    a.click();
    this.chart.chart.options.devicePixelRatio = 1;
    this.chartWidth = '100%';
  }

  public async exportAsCSV(event) {
    this.isLoading = true;
    const { startDate, endDate } = this.getStartEndDate();
    const { hourStart, hourEnd } = this.getHourFilter();
    this.dataService
      .dataCsvExportList(
        this.selectedFieldControl.value.identifier,
        startDate,
        endDate,
        hourStart,
        hourEnd,
        Array.from(this.availableSensors.values())
          .filter((sensor) =>
            sensor.available_fields
              .map((field) => field.identifier)
              .includes(this.selectedFieldControl.value.identifier)
          )
          .map((sensor) => sensor.influx_name)
      )
      .subscribe((result) => {
        // Result is a CSV blob, download it
        const a = document.createElement('a');
        a.href = URL.createObjectURL(result);
        const date = new Date();
        a.download = `${this.reportModuleService.currentReport.name}_${date
          .toLocaleDateString('de-AT')
          .replace(/\./g, '_')}.csv`;

        a.click();
        this.isLoading = false;
      });
  }

  public fieldHasEnabledSensors(field: string): boolean {
    const sensors = this.config.sensorFields.find((f) => f.fieldName === field);
    if (!sensors) {
      return false;
    }

    const enabledSensors = sensors.sensors.filter((s) => s.checked);
    return enabledSensors.length > 0;
  }

  compareFields(field1: any, field2: any): boolean {
    return field1 && field2 && field1.identifier === field2.identifier;
  }

  get numberEnabledSensors(): number {
    const enabledSensors = this.config.sensorFields
      .map((field) => field.sensors.filter((s) => s.checked))
      .flat();
    return enabledSensors.length;
  }

  aggregationChanged(event: MatSlideToggleChange) {
    this.reloadGraph(true);
  }

  public valuesCurrentlyDisplay(): number {
    if (!this.chart?.chart?.data?.datasets) {return 0;}
    if (!this.loadedData) {return 0;}
    if (!this.selectedFieldControl?.value) {return 0;}

    const selectedField = this.selectedFieldControl.value as SensorsSensorfield;

    if (this.loadedData.map.get(selectedField.identifier) === undefined)
      {return 0;}

    return Array.from(
      this.loadedData.map.get(selectedField.identifier).values()
    ).reduce((max, currentArray) => Math.max(max, currentArray.length), 0);
  }

  // Dataset visibility management methods
  public getDatasetVisibility(label: string): boolean {
    return this.datasetVisibilityMap.get(label) ?? true;
  }

  public setDatasetVisibility(label: string, visible: boolean): void {
    this.datasetVisibilityMap.set(label, visible);

    if (this.chart?.chart) {
      const datasetIndex = this.chart.chart.data.datasets.findIndex(
        (d) => d.label === label
      );
      if (datasetIndex >= 0) {
        this.chart.chart.setDatasetVisibility(datasetIndex, visible);
        this.chart.chart.update('none');
      }
    }
  }

  public updateLegendComponent(): void {
    // This method will be called by the legend component to update dataset states
    if (this.chart?.chart) {
      this.chartInstance = this.chart.chart;

      // Update legend component if available
      if (this.legendComponent) {
        setTimeout(() => {
          this.legendComponent.onChartUpdate();
        }, 0);
      }
    }
  }

  public onChartReady(): void {
    // Called when the chart is fully ready
    if (this.chart?.chart) {
      this.chartInstance = this.chart.chart;

      // Update legend component if available
      if (this.legendComponent) {
        this.legendComponent.onChartUpdate();
      }
    }
  }

  // Method to get the current chart instance for the legend
  public getChartInstance(): Chart<
    'line',
    CorrelationChartPoint[],
    unknown
  > | null {
    return this.chart?.chart || null;
  }

  // Method to force update the legend component
  public updateLegend(): void {
    if (this.legendComponent && this.chart?.chart) {
      // Ensure chart instance is set
      this.chartInstance = this.chart.chart;

      // Force update the legend
      setTimeout(() => {
        this.legendComponent.onChartUpdate();
      }, 50);
    }
  }

  public preserveDatasetVisibility(): void {
    if (!this.chart?.chart?.data?.datasets) {return;}

    // Store current visibility state for all datasets
    this.chart.chart.data.datasets.forEach((dataset, index) => {
      if (dataset.label) {
        this.datasetVisibilityMap.set(
          dataset.label,
          this.chart.chart.isDatasetVisible(index)
        );
      }
    });
  }

  public restoreDatasetVisibility(): void {
    if (!this.chart?.chart?.data?.datasets) {return;}

    // Restore visibility state for all datasets
    let restoredVisibility = false;
    this.chart.chart.data.datasets.forEach((dataset, index) => {
      if (dataset.label && this.datasetVisibilityMap.has(dataset.label)) {
        const shouldBeVisible = this.datasetVisibilityMap.get(dataset.label);
        if (shouldBeVisible !== undefined) {
          this.chart.chart.setDatasetVisibility(index, shouldBeVisible);
          restoredVisibility = true;
        }
      }
    });

    if (restoredVisibility) {
      this.chart.chart.update('none');
    }
  }

  /**
   * Apply global date range settings to this component
   */
  private applyGlobalDateRange(dateRange: GlobalDateRange): void {
    if (dateRange.kind === 'custom') {
      this.customDateRange = true;
      this.preDefinedDatePeriod.setValue(-1);
      if (dateRange.customStart) {
        this.correlationForm.controls['filterDateStart'].setValue(
          new Date(dateRange.customStart)
        );
      }
      if (dateRange.customEnd) {
        this.correlationForm.controls['filterDateEnd'].setValue(
          new Date(dateRange.customEnd)
        );
      }
    } else {
      this.customDateRange = false;
      const kindToIndex = (k: DateRangeKind): number => {
        switch (k) {
          case '24h':
            return 0;
          case '7d':
            return 1;
          case '14d':
            return 2;
          case '1m':
            return 3;
          case '1y':
            return 4;
          case 'zero':
          default:
            return 5;
        }
      };
      const idx = kindToIndex(dateRange.kind);
      if (
        Array.isArray(this.selectableTimePeriods) &&
        this.selectableTimePeriods[idx]
      ) {
        this.preDefinedDatePeriod.setValue(
          this.selectableTimePeriods[idx].value
        );
      }
    }
  }

  /**
   * Apply global smoothing settings to this component
   */
  private applyGlobalSmoothing(smoothing: SmoothingValue): void {
    // Find matching smoothing option
    const smoothingOption = this.selectableLowPassFilterPeriods.find(
      (opt) => opt.value === smoothing
    );
    if (smoothingOption) {
      this.preDefinedLowPassFilterPeriods.setValue(smoothingOption.value);
    }
  }

  ngOnDestroy(): void {
    this.globalSettingsSubscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    this.secondYAxisLoadCancel$.complete();
    this.mainAxisLoadCancel$.complete();
  }
}
